import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Plus, UserCog, Shield, Users, Link2, RefreshCw, Pencil, Power, PowerOff, Trash2, Save, Lock, History, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { initials, slug, fmtDateTime } from "@/lib/format";
import { Modal } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { TerritoryPicker, territoryLabel, emptyTerritory } from "@/components/TerritoryPicker";

const ROLE_INFO = {
  "Administrator": { c: "red", desc: "Semua modul, user, Odoo sync, konfigurasi, audit" },
  "Admin Sales": { c: "orange", desc: "Order, delivery, customer/pricelist, sync operasional" },
  "Sales Manager": { c: "green", desc: "Dashboard tim, approval, edit order, sync" },
  "Supervisor": { c: "blue", desc: "Dashboard, order & kunjungan hanya di wilayah (area/distributor) yang ditetapkan" },
  "Sales Representative": { c: "gray", desc: "Buat order & kunjungan sendiri" },
  "Finance": { c: "gray", desc: "Penagihan invoice, verifikasi pembayaran, AR term & AR aging" },
};

export default function Team() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [auditKey, setAuditKey] = useState(0);
  const bumpAudit = () => setAuditKey((k) => k + 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([api.get("/users"), api.get("/odoo/master")]);
      setUsers(u.data.data); setRoles(u.data.meta.roles || []); setEmployees(m.data.data.employees);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleCounts = roles.map((r) => ({ role: r, count: users.filter((u) => u.role === r).length }));

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "User dinonaktifkan" : "User diaktifkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Hapus permanen user ${u.name} (${u.email})? Tindakan ini tidak dapat dibatalkan.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const syncEmployees = async () => {
    try { const r = await api.post("/users/sync-employees"); toast.success(`${r.data.data.synced} karyawan Odoo tersinkron`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="team-page">
      <div className="section-head compact">
        <div><p className="eyebrow">PENGGUNA</p><h2>Tim & Akses</h2></div>
        <div className="team-header-actions">
          <button className="rj-btn rj-btn-ghost" onClick={syncEmployees} data-testid="sync-employees-btn"><RefreshCw size={16} /> Sinkron Karyawan</button>
          <button className="rj-btn rj-btn-primary" onClick={() => setCreateOpen(true)} data-testid="create-user-btn"><Plus size={16} /> Tambah User</button>
        </div>
      </div>

      <div className="access-grid five team-role-grid">
        {roleCounts.map((r) => (
          <div className={`access-card ${ROLE_INFO[r.role]?.c || "gray"}`} key={r.role}>
            <Shield />
            <div><strong>{r.role}</strong><span>{r.count} pengguna</span><p>{ROLE_INFO[r.role]?.desc}</p></div>
          </div>
        ))}
      </div>

      <div className="panel member-panel">
        <div className="panel-head"><div><h3><Users size={16} style={{ verticalAlign: -3 }} /> Daftar Pengguna</h3><p>{users.length} akun terhubung ke karyawan Odoo</p></div></div>
        {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : users.map((u) => (
          <div className="member-row managed" key={u.id} data-testid={`user-row-${u.email}`}>
            <div className="member-avatar">{initials(u.name)}</div>
            <div><strong>{u.name}</strong><span>{u.email}</span></div>
            <div><span className="member-role">{u.role}</span><span>{u.area}</span>
              {u.credit_approver && <span className="credit-approver-chip" data-testid={`credit-approver-chip-${u.email}`}>Approver kredit</span>}
              {u.role === "Supervisor" && territoryLabel(u.territory) && (
                <span className="credit-approver-chip territory" title={[...(u.territory.areas || [])].join(", ")} data-testid={`territory-chip-${u.email}`}>Wilayah: {territoryLabel(u.territory)}</span>
              )}</div>
            <div><span className="employee-link-status"><Link2 size={11} style={{ verticalAlign: -1 }} /> {u.employee_name || "—"}</span></div>
            <div><span>{u.phone_e164 || "-"}</span></div>
            <i className={u.active ? "online" : ""} />
            <div className="member-actions">
              <button onClick={() => setEditing(u)} data-testid={`edit-user-${u.email}`}><Pencil size={13} /> Edit</button>
              <button className={u.active ? "danger" : ""} onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.email}`} title={u.active ? "Nonaktifkan" : "Aktifkan"}>
                {u.active ? <PowerOff size={13} /> : <Power size={13} />}
              </button>
              {u.email !== "christabraham@gmail.com" && (
                <button className="danger" onClick={() => removeUser(u)} data-testid={`delete-user-${u.email}`} title="Hapus user"><Trash2 size={13} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      <RoleAccessPanel onSaved={bumpAudit} />
      <AccessAuditPanel refreshKey={auditKey} />

      {(editing || createOpen) && (
        <UserEditor user={editing} roles={roles} employees={employees}
          onClose={() => { setEditing(null); setCreateOpen(false); }}
          onSaved={() => { setEditing(null); setCreateOpen(false); load(); bumpAudit(); }} />
      )}
    </div>
  );
}

function AuditEntry({ a }) {
  if (a.type === "credit_approver") {
    return (
      <div className="access-audit-body">
        <strong>Approval batas kredit untuk <b>{a.target_name || a.target_email}</b> {a.enabled ? "diaktifkan" : "dinonaktifkan"}</strong>
      </div>
    );
  }
  if (a.type === "territory") {
    return (
      <div className="access-audit-body">
        <strong>Wilayah supervisor <b>{a.target_name || a.target_email}</b> diubah</strong>
        <div className="audit-chips">
          {(a.territory?.areas || []).map((x) => <span key={x} className="audit-chip add">{x}</span>)}
          {(a.territory?.distributor_ids || []).length > 0 && <span className="audit-chip add">{a.territory.distributor_ids.length} distributor</span>}
          {!territoryLabel(a.territory) && <span className="audit-chip rem">Dikosongkan</span>}
        </div>
      </div>
    );
  }
  if (a.type === "user_role") {
    return (
      <div className="access-audit-body">
        <strong>Role pengguna <b>{a.target_name || a.target_email}</b> diubah</strong>
        <span className="audit-role-change">{a.old_role} <ArrowRight size={12} /> <b>{a.new_role}</b></span>
      </div>
    );
  }
  return (
    <div className="access-audit-body">
      <strong>Hak akses role <b>{a.role}</b> diubah</strong>
      <div className="audit-chips">
        {(a.added || []).map((p) => <span key={`a-${p.perm}`} className="audit-chip add" title={p.perm}>+ {p.label}</span>)}
        {(a.removed || []).map((p) => <span key={`r-${p.perm}`} className="audit-chip rem" title={p.perm}>− {p.label}</span>)}
      </div>
    </div>
  );
}

function AccessAuditPanel({ refreshKey }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    api.get("/access-audit").then((r) => setLogs(r.data.data)).catch((e) => { toast.error(apiError(e)); setLogs([]); });
  }, [refreshKey]);

  return (
    <div className="panel access-audit-panel" data-testid="access-audit-panel">
      <div className="panel-head"><div><h3><History size={16} style={{ verticalAlign: -3 }} /> Log Perubahan Akses</h3>
        <p>Siapa mengubah hak akses role atau role pengguna, dan kapan.</p></div></div>
      {logs === null ? <div className="rj-loading"><Loader2 className="spin" /> Memuat log…</div> : logs.length === 0 ? (
        <div className="rec-empty" data-testid="access-audit-empty">Belum ada perubahan akses yang tercatat.</div>
      ) : (
        <div className="access-audit-list">
          {logs.map((a) => (
            <div className="access-audit-row" key={a.id} data-testid={`access-audit-${a.id}`}>
              <span className={`access-audit-icon ${a.type}`}>{a.type === "user_role" ? <UserCog size={15} /> : <Shield size={15} />}</span>
              <AuditEntry a={a} />
              <div className="access-audit-meta">
                <strong>{a.actor_name || a.actor_email}</strong>
                <small>{a.actor_email}</small>
                <small>{fmtDateTime(a.created_at)}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OWNER_EMAILS = ["christabraham@gmail.com", "rasajuaraid@gmail.com"];
const CREDIT_ROLES = ["Finance", "Finance Manager"];

function UserEditor({ user, roles, employees, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "", phone: user?.phone_e164 || "",
    role: user?.role || "Sales Representative", area: user?.area || "Unassigned",
    employee_odoo_id: user?.employee_odoo_id || "", active: user?.active ?? true,
    credit_approver: user?.credit_approver ?? false,
    territory: user?.territory || emptyTerritory,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim() || (!isEdit && !form.email.trim())) { toast.error("Nama & email wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { ...form, employee_odoo_id: form.employee_odoo_id ? Number(form.employee_odoo_id) : null,
        credit_approver: CREDIT_ROLES.includes(form.role) ? !!form.credit_approver : false,
        territory: form.role === "Supervisor" ? form.territory : null };
      if (isEdit) await api.patch(`/users/${user.id}`, payload);
      else await api.post("/users", payload);
      toast.success(isEdit ? "User diperbarui" : "User ditambahkan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="user-editor-dialog" title={isEdit ? "Edit Pengguna" : "Tambah Pengguna"}
      subtitle="Hubungkan akun dengan karyawan Odoo" testId="user-editor-dialog"
      footer={<><button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
        <button className="rj-btn rj-btn-primary" onClick={save} disabled={saving} data-testid="submit-user-btn">{saving ? <Loader2 size={16} className="spin" /> : "Simpan"}</button></>}>
      <div className="user-editor-form">
        <label>Nama<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></label>
        <label>Email (untuk kode OTP login)<input value={form.email} disabled={isEdit && OWNER_EMAILS.includes(user.email)} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></label>
        <label>Nomor WhatsApp<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0812xxxx" data-testid="user-phone" /></label>
        <label>Role<select className="rj-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">{roles.map((r) => <option key={r}>{r}</option>)}</select></label>
        <label>Area<input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="user-area" /></label>
        <label>Karyawan Odoo<select className="rj-select" value={form.employee_odoo_id} onChange={(e) => setForm({ ...form, employee_odoo_id: e.target.value })} data-testid="user-employee">
          <option value="">— Tidak ditautkan —</option>
          {employees.map((emp) => <option key={emp.odoo_id} value={emp.odoo_id}>{emp.name} ({emp.job_title})</option>)}
        </select></label>
        <label className="user-active-field" style={{ gap: 8 }}>
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="user-active" /> Aktif
        </label>
        {CREDIT_ROLES.includes(form.role) && (
          <label className="user-active-field credit-approver-field" style={{ gap: 8 }}>
            <input type="checkbox" checked={!!form.credit_approver} onChange={(e) => setForm({ ...form, credit_approver: e.target.checked })}
              data-testid="user-credit-approver" /> Boleh approve order yang melewati batas kredit (AR)
          </label>
        )}
        {form.role === "Supervisor" && <TerritoryPicker value={form.territory} onChange={(t) => setForm({ ...form, territory: t })} />}
      </div>
    </Modal>
  );
}

function RoleAccessPanel({ onSaved }) {
  const { refreshMe } = useAuth();
  const [data, setData] = useState(null);
  const [role, setRole] = useState(null);
  const [draft, setDraft] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/roles");
      const d = r.data.data;
      setData(d);
      const first = d.roles.find((x) => !d.locked.includes(x)) || d.roles[0];
      setRole(first);
      setDraft(new Set(d.permissions[first] || []));
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectRole = (rName) => { setRole(rName); setDraft(new Set(data.permissions[rName] || [])); };
  const locked = !!data?.locked?.includes(role);
  const toggle = (perm) => {
    if (locked) return;
    setDraft((s) => { const n = new Set(s); n.has(perm) ? n.delete(perm) : n.add(perm); return n; });
  };
  const dirty = data && role && JSON.stringify([...draft].sort()) !== JSON.stringify([...(data.permissions[role] || [])].sort());

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/roles/${encodeURIComponent(role)}`, { permissions: [...draft] });
      toast.success(`Hak akses "${role}" disimpan`);
      setData((d) => ({ ...d, permissions: { ...d.permissions, [role]: [...draft].sort() } }));
      refreshMe();
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (loading) return <div className="panel role-access-panel"><div className="rj-loading"><Loader2 className="spin" /> Memuat hak akses…</div></div>;
  if (!data) return null;

  return (
    <div className="panel role-access-panel" data-testid="role-access-panel">
      <div className="panel-head">
        <div><h3><Shield size={16} style={{ verticalAlign: -3 }} /> Hak Akses per Role</h3>
          <p>Centang menu &amp; fitur yang dapat diakses tiap role. Yang tidak dicentang tidak dapat diakses.</p></div>
        {!locked && <button className="rj-btn rj-btn-green rj-btn-sm" onClick={save} disabled={saving || !dirty} data-testid="save-role-perms-btn">
          {saving ? <Loader2 size={14} className="spin" /> : <><Save size={14} /> Simpan</>}</button>}
      </div>
      <div className="role-tabs" data-testid="role-tabs">
        {data.roles.map((r) => (
          <button key={r} className={`role-tab ${role === r ? "active" : ""}`} onClick={() => selectRole(r)} data-testid={`role-tab-${slug(r)}`}>
            {r}{data.locked.includes(r) && <Lock size={11} />}
          </button>
        ))}
      </div>
      {locked && <div className="role-locked-note"><Lock size={13} /> Role <b style={{ margin: "0 4px" }}>{role}</b> selalu memiliki akses penuh dan tidak dapat diubah.</div>}
      <div className="role-perm-groups">
        {data.catalog.map((g) => (
          <div className="role-perm-group" key={g.group}>
            <h4>{g.group}</h4>
            {g.items.map((it) => (
              <label key={it.perm} className={`perm-check ${draft.has(it.perm) ? "on" : ""}`}
                data-testid={`perm-${slug(role)}-${it.perm.replace(/:/g, "-")}`}>
                <input type="checkbox" checked={draft.has(it.perm)} disabled={locked} onChange={() => toggle(it.perm)} />
                <span>{it.label}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
